import React, { useState, useEffect } from 'react';
import api, { API_BASE } from '../utils/api';
import './UserManagement.css';

function UserManagement({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'viewer'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/users');
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users. Please try again later.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const isSuperAdmin = currentUser?.role === 'superadmin';
  const isAdmin = currentUser?.role === 'admin' || isSuperAdmin;

  if (!isAdmin) {
    return (
      <div className="user-management">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You do not have permission to access this page. Only administrators can manage users.</p>
        </div>
      </div>
    );
  }

  // Filter available roles based on current user's permissions
  const getAllRoles = () => [
    { value: 'superadmin', label: 'Superadmin' },
    { value: 'admin', label: 'Admin' },
    { value: 'staff', label: 'Staff' },
    { value: 'viewer', label: 'Viewer' },
    { value: 'public', label: 'Public' }
  ];
  
  // Admins cannot create superadmin or admin users
  const getAvailableRoles = () => {
    if (isSuperAdmin) {
      return getAllRoles();
    } else if (isAdmin) {
      return getAllRoles().filter(role => !['superadmin', 'admin'].includes(role.value));
    }
    return [];
  };
  
  const userRoles = getAvailableRoles();

  const getRoleColor = (role) => {
    switch (role) {
      case 'superadmin': return '#dc2626';
      case 'admin': return '#ea580c';
      case 'staff': return '#3b82f6';
      case 'viewer': return '#16a34a';
      case 'public': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const handleAdd = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'viewer'
    });
    setShowAddModal(true);
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role
    });
    setShowEditModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await api.delete(`/api/users/${id}`);
        await fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Failed to delete user. Please try again.');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (showAddModal) {
        await api.post('/api/users', formData);
        setShowAddModal(false);
      } else {
        await api.put(`/api/users/${selectedUser.id}`, formData);
        setShowEditModal(false);
      }
      await fetchUsers();
      setFormData({ name: '', email: '', password: '', role: 'viewer' });
    } catch (error) {
      console.error('Error saving user:', error);
      alert(error.response?.data?.error || 'Failed to save user. Please try again.');
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="user-management">
      <div className="management-header">
        <div>
          <h2>User Management</h2>
          <p>Add, edit, and manage user accounts and access levels</p>
        </div>
        <button onClick={handleAdd} className="add-button">
          Add New User
        </button>
      </div>

      <div className="user-stats">
        <div className="stat-card">
          <div className="stat-value">{users.length}</div>
          <div className="stat-label">Total Users</div>
        </div>
        {userRoles.map(role => {
          const count = users.filter(u => u.role === role.value).length;
          return (
            <div key={role.value} className="stat-card" style={{ borderLeftColor: getRoleColor(role.value) }}>
              <div className="stat-value">{count}</div>
              <div className="stat-label">{role.label}</div>
            </div>
          );
        })}
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search users by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="loading">Loading users...</div>
      ) : error ? (
        <div className="error-message">
          <h3>Error</h3>
          <p>{error}</p>
        </div>
      ) : (
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="5" className="empty-state">
                  No users found. Click "Add New User" to get started.
                </td>
              </tr>
            ) : (
              filteredUsers.map(user => (
                <tr key={user.id}>
                  <td className="name-cell">
                    <div className="user-info">
                      <div className="user-avatar">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      {user.name}
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <span 
                      className="role-badge"
                      style={{ backgroundColor: getRoleColor(user.role) }}
                    >
                      {getAllRoles().find(r => r.value === user.role)?.label || user.role}
                    </span>
                  </td>
                  <td>{user.createdAt}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleEdit(user)}
                        className="edit-button"
                        title="Edit"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="delete-button"
                        title="Delete"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      )}

      {(showAddModal || showEditModal) && (
        <div className="modal-overlay" onClick={() => {
          setShowAddModal(false);
          setShowEditModal(false);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{showAddModal ? 'Add New User' : 'Edit User'}</h3>
              <button
                className="close-button"
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="user-form">
              <div className="form-group">
                <label>
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Enter full name"
                />
              </div>

              <div className="form-group">
                <label>
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="user@example.com"
                />
              </div>

              <div className="form-group">
                <label>
                  Password {showAddModal ? '*' : '(leave blank to keep current)'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={showAddModal}
                  placeholder="Enter password"
                  minLength={8}
                />
                {showAddModal && (
                  <small className="form-hint">Minimum 8 characters</small>
                )}
              </div>

              <div className="form-group">
                <label>
                  User Role *
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                >
                  {userRoles.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
                <div className="role-descriptions">
                  <div className="role-desc-item">
                    <strong>Superadmin:</strong> Full system access including user management
                  </div>
                  <div className="role-desc-item">
                    <strong>Admin:</strong> Administrative access to all features
                  </div>
                  <div className="role-desc-item">
                    <strong>Staff:</strong> Can manage dogs, view risk matrix, and access streams
                  </div>
                  <div className="role-desc-item">
                    <strong>Viewer:</strong> Read-only access to view information
                  </div>
                  <div className="role-desc-item">
                    <strong>Public:</strong> Public access to missing dog wall only
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                  }}
                  className="cancel-button"
                >
                  Cancel
                </button>
                <button type="submit" className="submit-button">
                  {showAddModal ? 'Create User' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;

